import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { lazy, Suspense, ComponentType } from "react";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
import { ConversationProvider } from "@/contexts/ConversationContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LazyLoadErrorBoundary } from "@/components/LazyLoadErrorBoundary";
import { LoadingState } from "@/components/LoadingState";
import { PropertiesSkeleton } from "@/components/skeletons/PropertiesSkeleton";
import { PropertyDetailSkeleton } from "@/components/skeletons/PropertyDetailSkeleton";
import { MessagesSkeleton } from "@/components/skeletons/MessagesSkeleton";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleGuard } from "./components/RoleGuard";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { ScrollToTop } from "./components/ScrollToTop";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { ALL_STAFF_ROLES } from "./lib/permissions";

/** Activates version polling inside the router context */
const VersionChecker = () => { useVersionCheck(); return null; };

// Wrap BrowserRouter with Sentry routing instrumentation
const SentryRoutes = Sentry.withSentryRouting(Routes);

/**
 * Retry wrapper for lazy imports to handle chunk loading failures.
 * Automatically retries failed imports before showing error boundary.
 */
function lazyRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  retries = 2
): Promise<{ default: T }> {
  return factory().catch((error: Error) => {
    if (retries > 0 && error.message.includes('dynamically imported module')) {
      console.log(`[LazyRetry] Retrying import, ${retries} attempts left`);
      return new Promise<{ default: T }>((resolve) => setTimeout(resolve, 500))
        .then(() => lazyRetry(factory, retries - 1));
    }
    throw error;
  });
}

// Lazy load page components with auto-retry
const Index = lazy(() => lazyRetry(() => import("./pages/Index")));
const Auth = lazy(() => lazyRetry(() => import("./pages/Auth")));
const GuestRegistration = lazy(() => lazyRetry(() => import("./pages/GuestRegistration")));
const DemoGuestRegistration = lazy(() => lazyRetry(() => import("./pages/DemoGuestRegistration")));
const PartySize = lazy(() => lazyRetry(() => import("./pages/PartySize")));
const GuestList = lazy(() => lazyRetry(() => import("./pages/GuestList")));
const RegistrationConfirm = lazy(() => lazyRetry(() => import("./pages/RegistrationConfirm")));
const RegistrationReturn = lazy(() => lazyRetry(() => import("./pages/RegistrationReturn")));
const RegistrationSuccess = lazy(() => lazyRetry(() => import("./pages/RegistrationSuccess")));
const GuestPortal = lazy(() => lazyRetry(() => import("./pages/GuestPortal")));
const Dashboard = lazy(() => lazyRetry(() => import("./pages/Dashboard")));
const PropertiesList = lazy(() => lazyRetry(() => import("./pages/PropertiesList")));
const PropertyDetail = lazy(() => lazyRetry(() => import("./pages/PropertyDetail")));
const StatecDashboard = lazy(() => lazyRetry(() => import("./pages/StatecDashboard")));
const StatecPropertyDetail = lazy(() => lazyRetry(() => import("./pages/StatecPropertyDetail")));
const UserProfile = lazy(() => lazyRetry(() => import("./pages/UserProfile")));
const NotFound = lazy(() => lazyRetry(() => import("./pages/NotFound")));
const MigrateImages = lazy(() => lazyRetry(() => import("./pages/admin/MigrateImages")));
const StatecSubmissions = lazy(() => lazyRetry(() => import("./pages/admin/StatecSubmissions")));
const Bookings = lazy(() => lazyRetry(() => import("./pages/admin/Bookings")));
const NewBooking = lazy(() => lazyRetry(() => import("./pages/admin/NewBooking")));
const BookingDetail = lazy(() => lazyRetry(() => import("./pages/admin/BookingDetail")));
const GuestDetail = lazy(() => lazyRetry(() => import("./pages/admin/GuestDetail")));
const Messages = lazy(() => lazyRetry(() => import("./pages/admin/Messages")));
const Analytics = lazy(() => lazyRetry(() => import("./pages/admin/Analytics")));
const AIAnalytics = lazy(() => lazyRetry(() => import("./pages/admin/AIAnalytics")));
const Operations = lazy(() => lazyRetry(() => import("./pages/admin/Operations")));
const StayAgent = lazy(() => lazyRetry(() => import("./pages/admin/StayAgent")));
const RLHFAdmin = lazy(() => lazyRetry(() => import("./pages/admin/RLHFAdmin")));
const EscalationMonitor = lazy(() => lazyRetry(() => import("./pages/admin/EscalationMonitor")));
const KnowledgeReview = lazy(() => lazyRetry(() => import("./pages/admin/KnowledgeReview")));
const KnowledgeManagement = lazy(() => lazyRetry(() => import("./pages/admin/KnowledgeManagement")));
const TeamManagement = lazy(() => lazyRetry(() => import("./pages/admin/TeamManagement")));
const CleaningDashboard = lazy(() => lazyRetry(() => import("./pages/ops/CleaningDashboard")));
const MaintenanceDashboard = lazy(() => lazyRetry(() => import("./pages/ops/MaintenanceDashboard")));
const StaffPicksPage = lazy(() => lazyRetry(() => import("./pages/admin/StaffPicksPage")));

// Initialize QueryClient with error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      gcTime: 10 * 60 * 1000, // 10 min — data survives page switches
    },
    mutations: {
      retry: 1,
    },
  },
});

// Loading fallback component - elegant spinner for page transitions
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <div className="h-12 w-12 rounded-full border-4 border-muted" />
        <div className="absolute inset-0 h-12 w-12 rounded-full border-4 border-transparent border-t-primary animate-spin" />
      </div>
      <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <ComparisonProvider>
        <ConversationProvider>
          <TooltipProvider>
            <ErrorBoundary>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Toaster />
                <Sonner />
                <ScrollToTop />
                <VersionChecker />
                <LazyLoadErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <SentryRoutes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/register" element={<GuestRegistration />} />
                  <Route path="/register/demo" element={<DemoGuestRegistration />} />
                  <Route path="/register/party" element={<PartySize />} />
                  <Route path="/register/guests" element={<GuestList />} />
                  <Route path="/register/confirm" element={<RegistrationConfirm />} />
                  <Route path="/register/return" element={<RegistrationReturn />} />
                  <Route path="/register/success" element={<RegistrationSuccess />} />
                  <Route path="/guest/portal" element={<GuestPortal />} />
                  
                  {/* Protected Staff Routes — all roles get DashboardLayout, per-route RoleGuard */}
                  <Route element={
                    <ProtectedRoute allowedRoles={ALL_STAFF_ROLES}>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }>
                    {/* Dashboard: admin only */}
                    <Route path="/dashboard" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><Dashboard /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Analytics: admin only */}
                    <Route path="/analytics" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><Analytics /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Properties: all staff */}
                    <Route path="/properties" element={
                      <ErrorBoundary><Suspense fallback={<PropertiesSkeleton />}>
                        <PropertiesList />
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/properties/statec" element={
                      <ErrorBoundary><Suspense fallback={<PropertiesSkeleton />}>
                        <PropertiesList />
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/properties/ctie" element={
                      <ErrorBoundary><Suspense fallback={<PropertiesSkeleton />}>
                        <PropertiesList />
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/properties/soumissions" element={
                      <ErrorBoundary><Suspense fallback={<PropertiesSkeleton />}>
                        <PropertiesList />
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/properties/:id" element={
                      <ErrorBoundary><Suspense fallback={<PropertyDetailSkeleton />}>
                        <PropertyDetail />
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/properties/statec/:id" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <StatecPropertyDetail />
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Legacy routes */}
                    <Route path="/statec" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <PropertiesList />
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/statec/:id" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <StatecPropertyDetail />
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Profile: all staff */}
                    <Route path="/profile" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <UserProfile />
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Admin-only utility routes */}
                    <Route path="/admin/migrate-images" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><MigrateImages /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/submissions" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><StatecSubmissions /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Bookings: admin only */}
                    <Route path="/bookings" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><Bookings /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/bookings/new" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><NewBooking /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/bookings/:id" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><BookingDetail /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/guests/:id" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><GuestDetail /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Messages + AI Agent: admin only */}
                    <Route path="/messages" element={
                      <ErrorBoundary><Suspense fallback={<MessagesSkeleton />}>
                        <RoleGuard roles={['admin']}><Messages /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                    {/* Stay Agent - redirect to messages */}
                    <Route path="/stay-agent" element={
                      <Navigate to="/messages?conversation=stay-agent" replace />
                    } />

                    {/* Operations: admin + maintenance + cleaner (not PM) */}
                    <Route path="/operations" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin', 'maintenance', 'cleaner']}><Operations /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />

                    {/* AI section: admin only */}
                    <Route path="/ai-analytics" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><AIAnalytics /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/admin/rlhf" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><RLHFAdmin /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/escalations" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><EscalationMonitor /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Knowledge: admin only */}
                    <Route path="/knowledge-review" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><KnowledgeReview /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/knowledge" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><KnowledgeManagement /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Team: admin only */}
                    <Route path="/team" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><TeamManagement /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Staff Picks: admin only */}
                    <Route path="/staff-picks" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin']}><StaffPicksPage /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />

                    {/* Ops dashboards: role-specific (now inside DashboardLayout) */}
                    <Route path="/ops/cleaning" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin', 'cleaner']}><CleaningDashboard /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                    <Route path="/ops/maintenance" element={
                      <ErrorBoundary><Suspense fallback={<PageLoader />}>
                        <RoleGuard roles={['admin', 'maintenance']}><MaintenanceDashboard /></RoleGuard>
                      </Suspense></ErrorBoundary>
                    } />
                  </Route>

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                    </SentryRoutes>
                  </Suspense>
                </LazyLoadErrorBoundary>
              </BrowserRouter>
            </ErrorBoundary>
          </TooltipProvider>
        </ConversationProvider>
      </ComparisonProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
